/**
 * Setup Tasks — one-time checks shown on Day 1.
 *
 * Section is hidden once all items (initial sync + every assigned challenge join)
 * are complete. Items persist on the dashboard until checked off — they don't
 * reset per day. Stored as testing_day=1 by convention for one-time tests.
 */

import type { Tester, Verification } from "../../types";
import { Section, CheckRow } from "../Section";
import { CheckboxRow } from "../VerificationButtons";
import { SETUP_SYNC, challengeJoinKey } from "../../lib/conditionKeys";

interface SetupTasksSectionProps {
  profile: Tester;
  getStatus: (key: string, day: number) => Verification | null;
  setStatus: (input: { conditionKey: string; testingDay: number; status: "verified_ok" }) => Promise<void>;
}

export function SetupTasksSection({
  profile,
  getStatus,
  setStatus,
}: SetupTasksSectionProps) {
  const setupRow = getStatus(SETUP_SYNC, 1);
  const challengeRows = profile.challenges.map((c) => ({
    name: c,
    key: challengeJoinKey(c),
    row: getStatus(challengeJoinKey(c), 1),
  }));

  // Hide section once everything is checked off.
  const allComplete =
    setupRow?.status === "verified_ok" &&
    challengeRows.every((c) => c.row?.status === "verified_ok");
  if (allComplete) return null;

  const check = (key: string) => () =>
    setStatus({ conditionKey: key, testingDay: 1, status: "verified_ok" });

  return (
    <Section title="Setup tasks" meta="One-time">
      <CheckRow
        label="Connect my wearable to the Team RWB app"
        sub="Confirm the connection is active in the app."
        trailing={
          <CheckboxRow status={setupRow?.status ?? null} onCheck={check(SETUP_SYNC)} />
        }
      />
      {challengeRows.map(({ name, key, row }) => (
        <CheckRow
          key={key}
          label={
            <>
              I've joined <strong>{name}</strong> in the app
            </>
          }
          trailing={<CheckboxRow status={row?.status ?? null} onCheck={check(key)} />}
        />
      ))}
    </Section>
  );
}
